import { Component, type ErrorInfo, type ReactNode } from "react";
import { FailureCard } from "./FailureCard";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Changing this (for example the route path) resets the boundary. */
  resetKey?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/** Per-route boundary (rules/01 §4). Never a blank page: the failure card offers a way forward. */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(_error: Error, _info: ErrorInfo): void {
    // Sentry captures render errors once it is wired (rules/01 §12); nothing to log here.
  }

  override componentDidUpdate(prev: ErrorBoundaryProps): void {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <FailureCard
        title="This page hit a problem"
        description="Reloading usually fixes it. If it keeps happening, share the technical details with support."
        onRetry={() => this.setState({ error: null })}
        details={[error.name, error.message, error.stack].filter(Boolean).join("\n")}
      />
    );
  }
}
